
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    /*
| Developed by Dirupt
| Filename : verify.tsx
| Author : Philippe DESPLATS (philippe@dirupt.com)
*/
import React from 'react';
import { Box, Typography } from '@mui/material';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import * as Yup from 'yup';
import { toast } from 'sonner';
import { AxiosError, isAxiosError } from 'axios';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-mui';
import { m } from 'framer-motion';
import { SensklePageFactory } from '@/components/ssr/SensklePage';
import { createAuthServerSideProps } from '@/components/ssr/createServerSideProps';
import { UrlsConfig } from '@/config/urls.config';
import { MButton } from '@/components/common/MButton';
import { variantFade } from '@/components/animate';
import { UserResendVerifyDto } from '@/services/api/modules/user/user.dto';
import { UserEndpoint } from '@/services/api/modules/user/user.endpoint';
import { Link } from '@/components/common/MLink';
/*
|--------------------------------------------------------------------------
| Page
|--------------------------------------------------------------------------
*/
const VerifyPage = SensklePageFactory(() => {
    const { t } = useTranslation();
    const router = useRouter();
    const { token, email } = router.query;
    const [alreadyVerified, setAlreadyVerified] = React.useState(false);
    const showResendForm = React.useMemo(() => !email, [email]);
    // Formik & Yup
    // ----------------------------------------------------------------------------
    const initialValues: UserResendVerifyDto = {
        email: typeof email !== 'undefined' ? String(email) : '',
    };
    const validationSchema = React.useMemo(() => {
        return Yup.object().shape({
            email: Yup.string().email().required(),
        });
    }, []);
    // Verify methods
    // ----------------------------------------------------------------------------
    const verifyToken = React.useCallback(async () => {
        if (token) {
            try {
                await UserEndpoint.verify({ token: String(token) });
                toast.success(t('auth/verify:successes.verified'));
                await router.push(UrlsConfig.account.onboarding);
            }
            catch (error) {
                if (error instanceof AxiosError) {
                    switch (error?.response?.status) {
                        // Already verified
                        case 409:
                            toast.error(t('auth/verify:errors.alreadyVerified'), {
                                description: t('auth/verify:errors.alreadyVerifiedDescription'),
                            });
                            setAlreadyVerified(true);
                            console.log('test');
                            // await router.push(UrlsConfig.auth.login)
                            return;
                        case 422:
                            toast.error(t('auth/verify:errors.verificationFailed'));
                            break;
                    }
                }
                console.error(error);
                await router.push(UrlsConfig.auth.verify(), {
                    query: {
                        token: undefined,
                    },
                }, { shallow: true });
            }
        }
    }, [token, router, t]);
    React.useEffect(() => {
        void verifyToken();
    }, [verifyToken]);
    // Resend methods
    //--------------------------------------------------------------------------
    const handleResendEmail = async (values: UserResendVerifyDto) => {
        try {
            if (values.email) {
                if (!email) {
                    await router.push(UrlsConfig.auth.verify(values.email));
                }
                await UserEndpoint.resendVerify({ email: values.email });
                toast.success(t('auth/verify:successes.resendEmail'));
            }
        }
        catch (error) {
            if (isAxiosError(error)) {
                switch (error?.response?.status) {
                    // Rate limit
                    case 429:
                        toast.error(t('auth/verify:errors.rateLimit'));
                        break;
                    // Already verified
                    case 409:
                        toast.error(t('auth/verify:errors.alreadyVerified'), {
                            description: t('auth/verify:errors.alreadyVerifiedDescription'),
                        });
                        setAlreadyVerified(true);
                        await router.push(UrlsConfig.auth.login);
                }
            }
        }
    };
    return (<Box display={'flex'} flexDirection={'column'} justifyContent={'center'} gap={4} width={'100%'}>
			<Box display={'flex'} flexDirection={'column'} gap={2} textAlign={'center'} component={m.div} variants={variantFade().inLeft}>
				<Typography variant={'h1'}>{t('auth/verify:title')}</Typography>
				<Typography>
					{t(alreadyVerified
            ? 'auth/verify:subTitle.alreadyVerified'
            : showResendForm
                ? 'auth/verify:subTitle.withoutToken'
                : 'auth/verify:subTitle.withToken')}
				</Typography>
				{alreadyVerified ? (<MButton variant="contained" size={'large'} component={Link} href={UrlsConfig.account.onboarding}>
						{t('auth/verify:finishMyRegistration')}
					</MButton>) : null}
			</Box>
			{showResendForm && !alreadyVerified ? (<Formik initialValues={initialValues} onSubmit={handleResendEmail} validationSchema={validationSchema} enableReinitialize>
					{(formikProps) => (<Form>
							<Box display={'flex'} flexDirection={'column'} gap={2} component={m.div} variants={variantFade().inLeft}>
								<Field component={TextField} name={'email'} label={t('common:form.email')} fullWidth/>
								<MButton variant="contained" type="submit" size={'large'} disabled={formikProps.isSubmitting}>
									{t('auth/verify:resendButton')}
								</MButton>
							</Box>
						</Form>)}
				</Formik>) : null}
		</Box>);
});
/*
|--------------------------------------------------------------------------
| Configurations
|--------------------------------------------------------------------------
*/
VerifyPage.layout = 'auth';
export default VerifyPage;
/*
|--------------------------------------------------------------------------
| Server Side Props
|--------------------------------------------------------------------------
*/
const getServerSideProps = createAuthServerSideProps();

    async function __Next_Translate__getServerSideProps__1a09ebd47fd__(ctx) {
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/auth/verify',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1a09ebd47fd__ as getServerSideProps }
  